import { Avatar, ShouldRender } from '@/components/shared/misc'
import { getUserInitials } from '@/utils/static-helpers/initials'
import { MapPinIcon } from '@heroicons/react/24/outline'
import { ArrowRightIcon } from '@heroicons/react/24/solid'
import { HireGroup } from '@qureos/assets'
import { notify } from '@qureos/lib'
import { Button, Popover } from 'antd'
import Cookies from 'js-cookie'
import { useRouter } from 'next/router'
import React, { useRef } from 'react'
import { createJobV2Clone } from 'src/services/jobs'
import { Loading } from '../shared/basic'
import { HireFooter } from './HireFooter'
import { HireHeader } from './HireHeader'
import { segmentTrackCallback } from '@/utils/segment'
import { SegmentEventType } from '@qureos/types'

interface Props {
  jobData: any
  instantJobs?: any
  uaeJobs?: any
  saudiJobs?: any
}

interface SegmentData {
  id?: string
  slug: string
}

export const HireCard: React.FC<Props> = ({
  jobData = {},
  instantJobs,
  uaeJobs,
  saudiJobs
}) => {
  const [apiLoading, setApiLoading] = React.useState(false)
  const numberOfCandidates = jobData?.recommendations?.length || 0
  const router = useRouter()
  const token = Cookies.get('qureos_token')
  const ref = useRef(null)

  const triggerSegment = (event, data: SegmentData) => {
    const segmentData = {
      slug: data?.slug,
      ...(data?.id && { apprenticeId: data.id })
    }

    segmentTrackCallback(event, segmentData)
  }

  const handleClick = () => {
    ref.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const createInstantJob = async (isFindJob?: boolean) => {
    const id = jobData?._id?.toString()
    if (id) {
      setApiLoading(true)
      if (!token) {
        router.push(
          isFindJob
            ? `/jobs?apprenticeInstantJobId=${id}`
            : `/corporate/signup?instantJobId=${id}`
        )
      } else if (isFindJob) {
        router.push('/jobs')
      } else {
        try {
          const response = await createJobV2Clone(id)
          if (response) {
            notify({
              message: 'Job created successfully',
              type: 'success'
            })
            router.push(
              `/corporate/jobs/${response._id}?id=${response._id}&tab=1`
            )
          }
        } catch (error) {
          notify({
            message:
              error?.response?.data?.message ??
              'Unable to create instant job at the moment, please try again',
            type: 'error'
          })
        } finally {
          setApiLoading(false)
        }
      }
    }
  }
  const checkCompletenessProfile = profile => {
    return !!(
      profile?.preferences?.workStyle?.length &&
      profile?.photo &&
      profile?.settings?.profileHeadline &&
      profile?.nationality &&
      profile?.linkedIn &&
      profile?.professionalDetails?.educationHistory?.length &&
      profile?.professionalDetails?.educationStatus &&
      profile?.location &&
      profile?.phone &&
      profile?.preferences?.jobLocations?.length &&
      profile?.preferences?.personality &&
      profile?.preferences?.companyValues
    )
  }

  return (
    <div className="relative w-screen">
      <HireHeader createInstantJob={createInstantJob} />
      {apiLoading ? (
        <div className="pt-52 w-full">
          <Loading height="h-24" />
        </div>
      ) : (
        <>
          <div className="bg-lightHeading_2 p-5 md:p-20 md:pt-24 md:pb-48 flex flex-col md:flex-row justify-between">
            <div className="md:w-10/12 flex flex-col items-start">
              <div className="flex flex-col gap-y-5 lg:w-9/12">
                <h1 className="text-white md:text-5xl sm:text-4xl text-xl leading-tight font-extrabold">
                  Hire the Best <br />
                  <span className="text-yellow-400">
                    {jobData?.title}
                  </span> in {jobData?.location}
                </h1>
                <h4 className="text-white lg:text-lg md:text-sm text-xs">
                  Find and hire the best {jobData?.title} in {jobData?.location}{' '}
                  effortlessly. We have a large pool of {numberOfCandidates}{' '}
                  skilled {jobData?.title} professionals who are carefully
                  screened, guaranteeing you access to top-quality candidates.
                </h4>
              </div>
              <div className="flex gap-5 sm:flex-nowrap flex-wrap mt-8 w-full">
                <Button
                  type="text"
                  size="large"
                  onClick={() => {
                    handleClick()
                  }}
                  className="bg-buttonBlue z-40 text-white rounded-md font-semibold h-auto sm:w-max w-full sm:text-lg sm:px-6 sm:py-3 md:text-xl md:px-8 md:py-4 hover:cursor-pointer"
                >
                  Hire {jobData?.title} Now
                </Button>
                <Button
                  type="text"
                  size="large"
                  onClick={() => {
                    triggerSegment(SegmentEventType.Corp_LeadGenJobsPageCta, {
                      slug: jobData?.slug
                    })
                    createInstantJob(true)
                  }}
                  className="text-white border z-40 border-white rounded-md font-semibold h-auto sm:w-max w-full sm:text-lg sm:px-6 sm:py-3 md:text-xl md:px-8 md:py-4 hover:cursor-pointer"
                >
                  Find {jobData?.title} jobs
                </Button>
              </div>
            </div>
            <img
              src={HireGroup}
              width="100%"
              className="md:block hidden max-w-lg p-1"
            />
          </div>
          <div
            className="top-64 m-3 md:m-0 md:top-80 md:my-5 flex flex-col gap-5 w-11/12"
            ref={ref}
          >
            <span className="text-black md:mx-20 font-bold pt-10 sm:pt-10 md:pt-10 lg:pt-10 xl:pt-10">
              {jobData?.recommendations?.length
                ? `Here are just a few of our great ${jobData?.title} available to hire:`
                : ''}
            </span>
            {jobData?.recommendations
              ?.filter(item => {
                return (
                  !checkCompletenessProfile(item.candidate?.profile) &&
                  (item.reason || item?.candidate?.profile?.settings?.aboutMe)
                )
              })
              ?.slice(0, 10)
              .map((el, i) => (
                <div
                  key={i}
                  className="  md:mx-20 lg:w-7/12 border bg-white p-5 rounded-md"
                >
                  <div className="flex justify-between w-full">
                    <div className="w-max flex justify-start gap-2">
                      <Avatar
                        initials={getUserInitials(el.candidate.firstName)}
                        photo={undefined}
                        height="h-10"
                        width="w-10"
                        roundedSize="rounded-md"
                      />
                      <div>
                        <p className="font-bold sm:text-lg xs:text-base text-sm capitalize">
                          {el?.candidate?.firstName}
                          <span className="blur-sm select-none">
                            {' '}
                            {el?.candidate?.lastName || 'jhduerga'}
                          </span>
                        </p>
                        <p className="font-xs font-medium leading-3">
                          {el.candidate?.profile?.professionalDetails
                            ?.workHistory?.length ? (
                            <>
                              {' '}
                              {
                                el.candidate.profile.professionalDetails
                                  .workHistory[0].title
                              }
                              <span className="text-gray-500"> at </span>
                              {
                                el.candidate.profile.professionalDetails
                                  .workHistory[0].companyName
                              }
                            </>
                          ) : (
                            ''
                          )}
                        </p>
                        <p className="flex items-center justify-start gap-1 py-1">
                          <MapPinIcon className="w-4" /> {el.candidate.country}
                        </p>
                      </div>
                    </div>
                    <div className="w-min sm:block hidden">
                      <Button
                        onClick={() => {
                          triggerSegment(
                            SegmentEventType.Corp_LeadGenHireMeCta,
                            { slug: jobData?.slug, id: el?._id }
                          )
                          createInstantJob()
                        }}
                        className="flex group relative flex-nowrap rounded-lg font-bold text-center gap-1 w-max text-xs text-white bg-buttonBlue"
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span> Hire me </span>
                          <ArrowRightIcon
                            className="w-4 transition transform  
             ease-in-out hidden  
             group-hover:inline-block "
                          />
                        </div>
                      </Button>
                    </div>
                  </div>
                  <div className="w-full my-3 text-sm">
                    <p className="font-semibold text-blackHeading">
                      {(el.reason ||
                        el?.candidate?.profile?.settings?.aboutMe) &&
                        'Description:'}
                    </p>
                    <p className="text-gray-600 text-ellipsis line-clamp-2">
                      {el.reason
                        ? el.reason
                        : el?.candidate?.profile?.settings?.aboutMe || ''}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 text-sm">
                    {el?.candidate?.profile?.professionalDetails?.skills
                      ?.slice(0, 6)
                      ?.map(skill => (
                        <div
                          key={skill}
                          className="bg-buttonBlue bg-opacity-10 rounded-3xl px-3 text-buttonBlue flex-none font-inter"
                        >
                          {skill.name}
                        </div>
                      ))}{' '}
                    <ShouldRender
                      check={
                        el?.candidate?.profile?.professionalDetails?.skills?.slice(
                          6
                        ).length
                      }
                    >
                      <Popover
                        className="flex items-center justify-center gap-3"
                        content={
                          <div className="flex items-center justify-start w-auto max-w-md flex-wrap gap-2 ">
                            {el?.candidate?.profile?.professionalDetails?.skills
                              ?.slice(6)
                              ?.map(skill => (
                                <div
                                  key={skill}
                                  className="bg-buttonBlue bg-opacity-10 rounded-3xl px-3 text-buttonBlue flex-none font-inter"
                                >
                                  {skill.name}
                                </div>
                              ))}
                          </div>
                        }
                      >
                        <div className="bg-buttonBlue bg-opacity-10 rounded-3xl px-3 text-buttonBlue flex-none font-inter cursor-default">
                          +{' '}
                          {
                            el?.candidate?.profile?.professionalDetails?.skills?.slice(
                              6
                            ).length
                          }
                        </div>
                      </Popover>
                    </ShouldRender>
                  </div>
                  <div className="w-full mt-5 sm:hidden xs:block">
                    <Button
                      onClick={() => {
                        triggerSegment(SegmentEventType.Corp_LeadGenHireMeCta, {
                          slug: jobData?.slug,
                          id: el?._id
                        })
                        createInstantJob()
                      }}
                      className="font-bold w-full flex flex-nowrap items-center justify-center gap-1 text-xs text-white bg-buttonBlue rounded-lg"
                    >
                      Hire me{' '}
                      <ArrowRightIcon
                        className="w-4 transition transform  
             ease-in-out hidden  
             group-hover:inline-block "
                      />
                    </Button>
                  </div>
                </div>
              ))}
            <div className="md:mx-20 lg:w-7/12">
              <h2 className="py-4">Related jobs in {jobData?.country}</h2>
              <div className="flex items-center justify-start gap-4 flex-wrap">
                {instantJobs?.map(item => (
                  <div key={item?._id} className="bg-white p-4 rounded-lg">
                    <a
                      className="hover:text-qureosPrimary"
                      target="_blank"
                      href={`${process.env.APP_URL}/hire/${
                        item?.slug ?? item?._id
                      }`}
                      rel="noreferrer"
                    >
                      {item?.title}
                    </a>
                  </div>
                ))}
              </div>
            </div>
            <HireFooter
              footerData={[
                {
                  heading: 'Tools Comparison',
                  data: [
                    {
                      link: 'https://www.qureos.com/tools-comparison/ripplematch-vs-harver',
                      title: 'Ripplematch vs Harver'
                    },
                    {
                      link: 'https://www.qureos.com/tools-comparison/weworkremotely-vs-nodesk',
                      title: 'WeWorkRemotely vs Nodesk'
                    },
                    {
                      link: 'https://www.qureos.com/tools-comparison/unstop-vs-dice',
                      title: 'Unstop vs Dice'
                    },
                    {
                      link: 'https://www.qureos.com/tools-comparison/dice-vs-ripplematch',
                      title: 'Dice vs Ripplematch'
                    },
                    {
                      link: 'https://www.qureos.com/tools-comparison/hired-vs-harver',
                      title: 'Hired vs Harver'
                    },
                    {
                      link: 'https://www.qureos.com/tools-comparison/wellfound-vs-ripplematch',
                      title: 'WellFound vs Ripplematch'
                    },
                    {
                      link: 'https://www.qureos.com/tools-comparison/ripplematch-vs-weworkremotely',
                      title: 'Ripplematch vs WeWorkRemotely'
                    },
                    {
                      link: 'https://www.qureos.com/tools-comparison/weworkremotely-vs-hired',
                      title: 'WeWorkRemotely vs Hired'
                    },
                    {
                      link: 'https://www.qureos.com/tools-comparison/dice-vs-hired',
                      title: 'Dice vs Hired'
                    },
                    {
                      link: 'https://www.qureos.com/tools-comparison/wellfound-vs-dice',
                      title: 'WellFound vs Dice'
                    }
                  ]
                },
                {
                  heading: 'Tools Alternatives',
                  data: [
                    {
                      title: 'Mihnati Alternatives',
                      link: 'https://www.qureos.com/tools-comparison/best-mihnati-alternatives'
                    },
                    {
                      title: 'Wzayef Alternatives',
                      link: 'https://www.qureos.com/tools-comparison/best-wzayef-alternatives'
                    },
                    {
                      title: 'Laimoon Alternatives',
                      link: 'https://www.qureos.com/tools-comparison/best-laimoon-alternatives'
                    },
                    {
                      title: 'Akhtaboot Alternatives',
                      link: 'https://www.qureos.com/tools-comparison/best-akhtaboot-alternatives'
                    },
                    {
                      title: 'Gulftalent Alternatives',
                      link: 'https://www.qureos.com/tools-comparison/best-gulftalent-alternatives'
                    },
                    {
                      title: 'Naukrigulf Alternatives',
                      link: 'https://www.qureos.com/tools-comparison/best-naukrigulf-alternatives'
                    },
                    {
                      title: 'Grabjobs Alternatives',
                      link: 'https://www.qureos.com/tools-comparison/best-grabjobs-alternatives'
                    },
                    {
                      title: 'Otta Alternatives',
                      link: 'https://www.qureos.com/tools-comparison/best-otta-alternatives'
                    },
                    {
                      title: 'Zoho Recruit Alternatives',
                      link: 'https://www.qureos.com/tools-comparison/best-zoho-recruit-alternatives'
                    },
                    {
                      title: 'RippleMatch Alternatives',
                      link: 'https://www.qureos.com/tools-comparison/best-ripplematch-alternatives'
                    }
                  ]
                },
                {
                  heading: 'Hiring Resources',
                  data: [
                    {
                      title: 'Interview Tips',
                      link: 'https://www.qureos.com/hiring-advice/interview'
                    },
                    {
                      title: 'Cultivating a Winning Culture',
                      link: 'https://www.qureos.com/hiring-advice/culture'
                    },
                    {
                      title: 'Employee Retention',
                      link: 'https://www.qureos.com/hiring-advice/employee-retention'
                    },
                    {
                      title: 'Hiring Advice',
                      link: 'https://www.qureos.com/hiring-advice/hiring'
                    },
                    {
                      title: 'Best Practices',
                      link: 'https://www.qureos.com/hiring-advice/tips'
                    },
                    {
                      title: 'Job Description Templates',
                      link: 'https://www.qureos.com/hiring-advice/job-description'
                    }
                  ]
                },
                {
                  heading: 'Hiring Tools',
                  data: [
                    {
                      title: 'Iris - AI Recruitment Platform',
                      link: 'https://www.qureos.com/for-recruiters'
                    },
                    {
                      title: 'Job Description Generator',
                      link: 'https://app.qureos.com/job-description-generator'
                    },
                    {
                      title: 'Interview Questions Generator',
                      link: 'https://app.qureos.com/job-description-generator'
                    }
                  ]
                },
                {
                  heading: 'In UAE',
                  data: uaeJobs.map(item => ({
                    link: `${process.env.APP_URL}/hire/${
                      item?.slug ?? item?._id
                    }`,
                    title: ` Hire ${item?.title}`
                  }))
                },
                {
                  heading: 'In KSA',
                  data: saudiJobs.map(item => ({
                    link: `${process.env.APP_URL}/hire/${
                      item?.slug ?? item?._id
                    }`,
                    title: ` Hire ${item?.title}`
                  }))
                }
              ]}
            />
          </div>
        </>
      )}
    </div>
  )
}
